import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { FeeAmount, Pool, TICK_SPACINGS, TickMath, tickToPrice } from '@kodiak-finance/v3-sdk'
import { useConnection } from 'hooks/useConnection'
import { usePoolActiveLiquidity } from 'hooks/usePoolTickData'
import JSBI from 'jsbi'
import { useCallback, useEffect, useMemo, useState } from 'react'

export type PoolLiquidityChartPoint = {
  index: number
  tick: number
  price0: number
  price1: number
  activeLiquidity: number
  isCurrent: boolean
  tvlToken0: number
  tvlToken1: number
}

type TokenInput = {
  address?: string
  symbol?: string
  name?: string
  decimals?: number
}

export interface UseV3PoolLiquidityChartParams {
  token0: TokenInput
  token1: TokenInput
  feeTier?: number | null
  enabled?: boolean
}

export interface UseV3PoolLiquidityChartResult {
  points?: PoolLiquidityChartPoint[]
  currentPoint?: PoolLiquidityChartPoint
  isLoading: boolean
  isUninitialized: boolean
  isError: boolean
  error: unknown
  canExtendRange: boolean
  extendRange: () => void
}

const asFeeAmount = (feeTier?: number | null): FeeAmount | undefined => {
  if (feeTier == null) return undefined

  switch (feeTier) {
    case FeeAmount.LOWEST:
    case FeeAmount.LOW:
    case FeeAmount.MEDIUM:
    case FeeAmount.HIGH:
    case FeeAmount.EXTRA:
      return feeTier
    default:
      return undefined
  }
}

const buildToken = (chainId: number | undefined, token: TokenInput) => {
  if (!chainId) return undefined

  const { address, decimals } = token
  if (!address || decimals == null) return undefined

  try {
    return new Token(chainId, address, decimals, token.symbol, token.name)
  } catch {
    return undefined
  }
}

const MAX_UINT128 = JSBI.BigInt('340282366920938463463374607431768211455')
const DEFAULT_SURROUNDING_TICKS = 200
const SURROUNDING_TICKS_INCREMENT = 20
const MAX_SURROUNDING_TICKS = 20000
const PRICE_FIXED_DIGITS = 4

export function useV3PoolLiquidityChart({
  token0,
  token1,
  feeTier,
  enabled = true,
}: UseV3PoolLiquidityChartParams): UseV3PoolLiquidityChartResult {
  const { chainId } = useConnection()
  const feeAmount = useMemo(() => asFeeAmount(feeTier), [feeTier])

  const hasTokenMetadata =
    Boolean(token0.address) && token0.decimals !== undefined && Boolean(token1.address) && token1.decimals !== undefined

  const shouldFetch = enabled && Boolean(feeAmount) && hasTokenMetadata

  const currencyA = useMemo(
    () => (shouldFetch ? buildToken(chainId, token0) : undefined),
    [chainId, token0, shouldFetch]
  )
  const currencyB = useMemo(
    () => (shouldFetch ? buildToken(chainId, token1) : undefined),
    [chainId, token1, shouldFetch]
  )

  const [points, setPoints] = useState<PoolLiquidityChartPoint[] | undefined>(undefined)
  const [processing, setProcessing] = useState(false)
  const [surroundingTicks, setSurroundingTicks] = useState(DEFAULT_SURROUNDING_TICKS)

  const { isLoading, isUninitialized, isError, error, data, activeTick } = usePoolActiveLiquidity(
    currencyA,
    currencyB,
    shouldFetch ? feeAmount : undefined,
    surroundingTicks
  )

  useEffect(() => {
    setSurroundingTicks(DEFAULT_SURROUNDING_TICKS)
  }, [currencyA, currencyB, feeAmount, shouldFetch])

  useEffect(() => {
    let cancelled = false

    if (!shouldFetch || !data?.length || !currencyA || !currencyB || !feeAmount) {
      setPoints(undefined)
      return
    }
    const token0Ref = currencyA ?? currencyB
    const token1Ref = currencyB ?? currencyA
    const tickSpacing = TICK_SPACINGS[feeAmount]
    if (!token0Ref || !token1Ref || !Number.isInteger(tickSpacing) || tickSpacing <= 0) {
      setPoints(undefined)
      return
    }

    const priceForTick = (tick: number) => tickToPrice(token0Ref, token1Ref, tick).toFixed(PRICE_FIXED_DIGITS)

    const sortedTicks = [...data].sort((a, b) => a.tick - b.tick)
    const activeIndex = sortedTicks.findIndex((tick) => tick.tick === activeTick)

    // Mirror legacy density chart by filling every tick step around the active tick, even without initialized liquidity.
    const buildDenseTicks = (activeTickValue: number) => {
      if (activeIndex === -1) {
        return sortedTicks
      }

      const ascending: typeof sortedTicks = []
      let ascActualIndex = activeIndex
      let lastActualAsc = {
        ...sortedTicks[activeIndex],
        price0: sortedTicks[activeIndex].price0 ?? priceForTick(activeTickValue),
      }

      for (let step = 0; step <= surroundingTicks; step++) {
        const tick = activeTickValue + step * tickSpacing
        if (tick > TickMath.MAX_TICK) {
          break
        }

        const currentActual = sortedTicks[ascActualIndex]
        if (currentActual && currentActual.tick === tick) {
          const entry = {
            ...currentActual,
            price0: currentActual.price0 ?? priceForTick(tick),
          }
          ascending.push(entry)
          lastActualAsc = entry
          ascActualIndex += 1
        } else {
          ascending.push({
            tick,
            liquidityActive: lastActualAsc?.liquidityActive ?? JSBI.BigInt(0),
            liquidityNet: JSBI.BigInt(0),
            liquidityGross: JSBI.BigInt(0),
            price0: priceForTick(tick),
          })
        }
      }

      const descending: typeof sortedTicks = []
      let descActualIndex = activeIndex
      const initialActual = {
        ...sortedTicks[activeIndex],
        price0: sortedTicks[activeIndex].price0 ?? priceForTick(activeTickValue),
      }
      const initialNet = initialActual.liquidityNet ?? JSBI.BigInt(0)
      let carryLiquidityBelow = (() => {
        const tentative = JSBI.subtract(initialActual.liquidityActive, initialNet)
        return JSBI.lessThan(tentative, JSBI.BigInt(0)) ? JSBI.BigInt(0) : tentative
      })()

      for (let step = 0; step <= surroundingTicks; step++) {
        const tick = activeTickValue - step * tickSpacing
        if (tick < TickMath.MIN_TICK) {
          break
        }

        const currentActual = descActualIndex >= 0 ? sortedTicks[descActualIndex] : undefined
        if (currentActual && currentActual.tick === tick) {
          const entry = {
            ...currentActual,
            price0: currentActual.price0 ?? priceForTick(tick),
          }
          descending.push(entry)
          const net = entry.liquidityNet ?? JSBI.BigInt(0)
          const tentativeBelow = JSBI.subtract(entry.liquidityActive, net)
          carryLiquidityBelow = JSBI.lessThan(tentativeBelow, JSBI.BigInt(0)) ? JSBI.BigInt(0) : tentativeBelow
          descActualIndex -= 1
        } else {
          descending.push({
            tick,
            liquidityActive: carryLiquidityBelow,
            liquidityNet: JSBI.BigInt(0),
            liquidityGross: JSBI.BigInt(0),
            price0: priceForTick(tick),
          })
        }
      }

      const descendingWithoutActive = descending.slice(1).reverse()
      return [...descendingWithoutActive, ...ascending]
    }

    const denseTicks = activeTick != null ? buildDenseTicks(activeTick) : sortedTicks
    const maxAmountToken0 = CurrencyAmount.fromRawAmount(currencyA, MAX_UINT128.toString())

    const buildPoints = async () => {
      setProcessing(true)
      try {
        const formatted = await Promise.all(
          denseTicks.map(async (tick, index, arr) => {
            const activeLiquidity = Number.parseFloat(tick.liquidityActive.toString())
            const price0Raw = Number.parseFloat(tick.price0)
            const price0 = Number.isFinite(price0Raw) && price0Raw > 0 ? price0Raw : NaN
            const price1 = Number.isFinite(price0) && price0 > 0 ? 1 / price0 : NaN

            const resolvedPrice0 =
              Number.isFinite(price0) && price0 > 0
                ? price0
                : Number.isFinite(tick.tick)
                ? Number.parseFloat(tickToPrice(token0Ref, token1Ref, tick.tick).toSignificant(10))
                : NaN

            const resolvedPrice1 =
              Number.isFinite(price1) && price1 > 0 && Number.isFinite(resolvedPrice0)
                ? 1 / resolvedPrice0
                : Number.isFinite(tick.tick)
                ? Number.parseFloat(tickToPrice(token1Ref, token0Ref, tick.tick).toSignificant(10))
                : NaN

            const previousTick = arr[index - 1]

            let tvlToken0 = 0
            let tvlToken1 = 0

            if (tickSpacing && tickSpacing > 0) {
              try {
                const sqrtCurrent = TickMath.getSqrtRatioAtTick(tick.tick)
                const mockTicks = [
                  {
                    index: tick.tick - tickSpacing,
                    liquidityGross: tick.liquidityGross,
                    liquidityNet: JSBI.multiply(tick.liquidityNet, JSBI.BigInt('-1')),
                  },
                  {
                    index: tick.tick,
                    liquidityGross: tick.liquidityGross,
                    liquidityNet: tick.liquidityNet,
                  },
                ]

                const pool = new Pool(
                  currencyA,
                  currencyB,
                  feeAmount,
                  sqrtCurrent,
                  tick.liquidityActive,
                  tick.tick,
                  mockTicks
                )

                const sqrtLimit = previousTick ? TickMath.getSqrtRatioAtTick(previousTick.tick) : undefined
                const [token1Amount] = await pool.getOutputAmount(maxAmountToken0, sqrtLimit)

                const amount1 = token1Amount ? parseFloat(token1Amount.toExact()) : 0
                const amount0 =
                  Number.isFinite(resolvedPrice1) && Number.isFinite(amount1) ? amount1 * (resolvedPrice1 as number) : 0

                tvlToken0 = Number.isFinite(amount0) ? amount0 : 0
                tvlToken1 = Number.isFinite(amount1) ? amount1 : 0
              } catch {
                tvlToken0 = 0
                tvlToken1 = 0
              }
            }

            const point: PoolLiquidityChartPoint = {
              index,
              tick: tick.tick,
              price0: resolvedPrice0,
              price1: resolvedPrice1,
              activeLiquidity,
              isCurrent: tick.tick === activeTick,
              tvlToken0,
              tvlToken1,
            }

            return point
          })
        )

        const sanitized = formatted.filter(
          (point) =>
            Number.isFinite(point.price0) && Number.isFinite(point.price1) && Number.isFinite(point.activeLiquidity)
        )

        for (let i = 1; i < sanitized.length; i++) {
          sanitized[i - 1].tvlToken0 = sanitized[i].tvlToken0
          sanitized[i - 1].tvlToken1 = sanitized[i].tvlToken1
        }

        if (!cancelled) {
          setPoints(sanitized.length ? sanitized : undefined)
        }
      } finally {
        if (!cancelled) {
          setProcessing(false)
        }
      }
    }

    buildPoints()

    return () => {
      cancelled = true
    }
  }, [
    shouldFetch,
    data,
    currencyA,
    currencyB,
    feeAmount,
    activeTick,
    token0.decimals,
    token1.decimals,
    surroundingTicks,
  ])

  const currentPoint = useMemo(() => points?.find((point) => point.isCurrent), [points])

  const canExtendRange = surroundingTicks < MAX_SURROUNDING_TICKS
  const extendRange = useCallback(() => {
    if (!canExtendRange) return
    setProcessing(true)
    setSurroundingTicks((prev) =>
      prev >= MAX_SURROUNDING_TICKS ? prev : Math.min(prev + SURROUNDING_TICKS_INCREMENT, MAX_SURROUNDING_TICKS)
    )
  }, [canExtendRange])

  return {
    points,
    currentPoint,
    isLoading: shouldFetch ? isLoading || processing : true,
    isUninitialized: shouldFetch ? isUninitialized : true,
    isError: shouldFetch ? isError : false,
    error: shouldFetch ? error : undefined,
    canExtendRange,
    extendRange,
  }
}
